import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/stores/auth.store";
import { usePermissions } from "@/hooks/usePermissions";
import { useOpenTask } from "@/hooks/useOpenTask";
import {
  completeTask,
  listTasks,
  reopenTask,
} from "@/api/tasks";
import { listMembers } from "@/api/projects";
import type {
  Company,
  ProjectMember,
  TaskRead,
} from "@/api/types";
import { TaskItem } from "./TaskItem";
import { TaskCreateDialog } from "./TaskCreateDialog";
import { TaskDetailSheet } from "./TaskDetailSheet";
import { PRIORITY_RANK } from "./priority";

const VISIBLE_LIMIT = 5;

interface CompanyTasksCardProps {
  projectSlug: string;
  company: Pick<Company, "id" | "name" | "domain">;
}

export function CompanyTasksCard({
  projectSlug,
  company,
}: CompanyTasksCardProps) {
  const perms = usePermissions();
  const currentUser = useAuthStore((s) => s.user);
  const [tasks, setTasks] = useState<TaskRead[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailTask, setDetailTask] = useOpenTask(
    `company-tasks:${company.id}`,
    tasks,
  );
  const [editTask, setEditTask] = useState<TaskRead | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listTasks(projectSlug, {
        company_id: company.id,
        page_size: 50,
      });
      // Pending first (incomplete by priority desc + due_at asc), then completed.
      const pending = res.items.filter((t) => !t.is_completed);
      const completed = res.items.filter((t) => t.is_completed);
      pending.sort((a, b) => {
        const pr = PRIORITY_RANK[b.priority] - PRIORITY_RANK[a.priority];
        if (pr !== 0) return pr;
        const ad = a.due_at ?? "9999-12-31";
        const bd = b.due_at ?? "9999-12-31";
        return ad.localeCompare(bd);
      });
      completed.sort((a, b) =>
        (b.completed_at ?? "").localeCompare(a.completed_at ?? ""),
      );
      setTasks([...pending, ...completed]);
      setPendingCount(pending.length);
    } catch {
      // Silent — the section is secondary; the user can still navigate.
    } finally {
      setLoading(false);
    }
  }, [projectSlug, company.id]);

  useEffect(() => {
    if (!perms.canViewTasks) return;
    void load();
  }, [perms.canViewTasks, load]);

  // Members are needed by the create dialog.
  useEffect(() => {
    if (!perms.canCreateTasks) return;
    let cancelled = false;
    listMembers(projectSlug)
      .then((m) => !cancelled && setMembers(m))
      .catch(() => !cancelled && setMembers([]));
    return () => {
      cancelled = true;
    };
  }, [projectSlug, perms.canCreateTasks]);

  async function handleToggle(task: TaskRead) {
    try {
      const updated = task.is_completed
        ? await reopenTask(projectSlug, task.id)
        : await completeTask(projectSlug, task.id);
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? updated : t)),
      );
      setPendingCount((c) => c + (updated.is_completed ? -1 : 1));
    } catch (err) {
      toast.error(extractMessage(err));
    }
  }

  function handleSaved(task: TaskRead) {
    setTasks((prev) => {
      const idx = prev.findIndex((t) => t.id === task.id);
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = task;
        return next;
      }
      return [task, ...prev];
    });
    setPendingCount((c) => (task.is_completed ? c : c + 1));
  }

  if (!perms.canViewTasks) return null;

  const visible = tasks.slice(0, VISIBLE_LIMIT);
  const hasMore = tasks.length > VISIBLE_LIMIT;

  return (
    <section className="rounded-plane bg-white p-5 shadow-plane">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-slate-500">
          Tasks
          {pendingCount > 0 && (
            <span className="rounded-full bg-slate-100 px-1.5 py-0 text-[10px] font-semibold text-slate-600 normal-case">
              {pendingCount} pending
            </span>
          )}
        </h2>
        {perms.canCreateTasks && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setCreateOpen(true)}
            className="text-slate-600"
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add task
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-xs text-slate-400">
          <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
          Loading tasks…
        </div>
      ) : visible.length === 0 ? (
        <p className="py-2 text-sm text-slate-400">No tasks for this company.</p>
      ) : (
        <div className="-mx-5 border-t border-slate-900/[0.06]">
          {visible.map((task) => (
            <TaskItem
              key={task.id}
              task={task}
              projectSlug={projectSlug}
              showCompany={false}
              showAssignee
              canEdit={perms.canEditTasks}
              canDelete={false}
              onToggleComplete={handleToggle}
              onOpenDetail={(t) => setDetailTask(t)}
            />
          ))}
        </div>
      )}

      {hasMore && (
        <div className="mt-3 text-right">
          <Link
            to={`/projects/${projectSlug}/tasks?company_id=${company.id}`}
            className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-500"
          >
            View all tasks
            <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      )}

      {perms.canCreateTasks && (
        <TaskCreateDialog
          projectSlug={projectSlug}
          open={createOpen}
          onOpenChange={setCreateOpen}
          members={members}
          defaultAssigneeId={currentUser?.id ?? ""}
          lockedCompany={company}
          onSaved={handleSaved}
        />
      )}

      <TaskDetailSheet
        task={detailTask}
        open={!!detailTask}
        onOpenChange={(o) => {
          if (!o) setDetailTask(null);
        }}
        projectSlug={projectSlug}
        onTaskUpdated={(updated) => {
          setTasks((prev) =>
            prev.map((t) => (t.id === updated.id ? updated : t)),
          );
          setDetailTask(updated);
          // Pending count may shift if completion state flipped.
          setPendingCount((c) => {
            const was = detailTask?.is_completed ?? false;
            const now = updated.is_completed;
            if (was === now) return c;
            return c + (now ? -1 : 1);
          });
        }}
        onTaskDeleted={(id) => {
          setTasks((prev) => prev.filter((t) => t.id !== id));
          setPendingCount((c) =>
            detailTask && !detailTask.is_completed ? Math.max(0, c - 1) : c,
          );
          setDetailTask(null);
        }}
        onAskEdit={
          perms.canEditTasks
            ? (t) => {
                setDetailTask(null);
                setEditTask(t);
              }
            : undefined
        }
      />

      {perms.canEditTasks && editTask && (
        <TaskCreateDialog
          mode="edit"
          task={editTask}
          projectSlug={projectSlug}
          open={!!editTask}
          onOpenChange={(o) => {
            if (!o) setEditTask(null);
          }}
          members={members}
          lockedCompany={company}
          onSaved={(updated) => {
            handleSaved(updated);
            setEditTask(null);
          }}
        />
      )}
    </section>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Couldn't update the task.";
}
